import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
  CircularProgress,
  IconButton,
  ButtonBase,
} from "@material-ui/core";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import { Pagination } from "@material-ui/lab";
import { DatePicker } from "@material-ui/pickers";
import { useLazyQuery, useMutation, useQuery } from "@apollo/react-hooks";
import gql from "graphql-tag";
import moment from "moment";

import Table from "../../components/Table";
import { Card, CardContent, CardActions } from "../../components/Card";
import Button from "../../components/Button";
import Grid from "../../components/Grid";
import LoadingModal from "../../components/LoadingModal";
import { TextInput, NumberInput } from "../../components/Form";
import { useAlert } from "../../components/Alert";

const GET_ACCOUNTMANAGEMENTS = gql`
  query accountManagements($latest: Boolean, $pageSize: Int, $page: Int) {
    accountManagements(latest: $latest, pageSize: $pageSize, page: $page) {
      count
      pageCount
      contents {
        id
        createTo {
          id
          fullName
        }
        month
        cost
        createdAt
      }
    }
  }
`;

const GET_ACCOUNTMANAGEMENT = gql`
  query accountManagement($id: Int!) {
    accountManagement(id: $id) {
      id
      createTo {
        id
        fullName
      }
      month
      cost
      file {
        filename
        mimetype
        encoding
        location
      }
    }
  }
`;

const SAVE_ACCOUNTMANAGEMENT = gql`
  mutation saveAccountManagement(
    $accountManagementInput: AccountManagementInput!
  ) {
    saveAccountManagement(accountManagementInput: $accountManagementInput)
  }
`;

const DELETE_ACCOUNTMANAGEMENT = gql`
  mutation deleteAccountManagement($id: Int!) {
    deleteAccountManagement(id: $id)
  }
`;

const UPLOAD_FILE = gql`
  mutation uploadFile($file: Upload!) {
    uploadFile(file: $file) {
      filename
      mimetype
      encoding
      location
    }
  }
`;

export default function AccountManageTable() {
  const Alert = useAlert();
  const [newData, setNewData] = useState({
    month: new Date(),
    cost: "",
    open: false,
    page: 1,
    pageMax: undefined,
    pageSize: 10,
    pageArray: [],
    data: [],
  });

  function changeDataListener(key, v) {}
  function changeData(key, v, rerender = false) {
    if (rerender) setNewData((prev) => ({ ...prev, [key]: v }));
    else setNewData((prev) => Object.assign(prev, { [key]: v }));
    if (typeof changeDataListener === "function") changeDataListener(key, v);
  }
  function thousandsSeparator(number) {
    if (typeof number === "number" || typeof number === "string") {
      return number.toLocaleString("en-US");
    } else {
      return null;
    }
  }
  const { data, refetch, loading } = useQuery(GET_ACCOUNTMANAGEMENTS, {
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    variables: {
      latest: false,
      pageSize: 10,
      page: newData.page,
    },
    onError(error) {
      Alert.notice(`${error.message.replace("GraphQL error: ", "")}`);
    },
  });

  const [deleteAccountManagement, { loading: deleteAccountManagementLoading }] =
    useMutation(DELETE_ACCOUNTMANAGEMENT, {
      onCompleted({ deleteAccountManagement }) {
        if (deleteAccountManagement) {
          return Alert.alert("", "刪除成功！", [
            {
              text: "確定",
              onPress: refetch,
              type: "ok",
            },
          ]);
        } else {
          return Alert.alert("", "刪除失敗！", [
            {
              text: "確定",
              type: "ok",
            },
          ]);
        }
      },
      onError(error) {
        Alert.notice(`${error.message.replace("GraphQL error: ", "")}`);
      },
    });
  if (loading) {
    return (
      <Grid container item justify="center">
        <CircularProgress color="secondary" />
      </Grid>
    );
  } else {
    return (
      <Card>
        <LoadingModal loading={deleteAccountManagementLoading} />
        <CardContent>
          <Grid container spacing={1} justify="flex-end">
            <Grid item>
              <Button
                color="primary"
                variant="contained"
                fullWidth
                onPress={() => {
                  changeData("open", true, true);
                }}
              >
                新增營運成本
              </Button>
            </Grid>
            <OperatingCostDialog
              id={newData.id}
              open={newData.open}
              onClose={(v) => {
                if (v === "refetch") refetch();
                changeData("open", false, true);
              }}
            />
          </Grid>
        </CardContent>
        <Divider />
        <CardContent>
          <Table
            data={data?.accountManagements.contents}
            tableWidth={2}
            header={["營運月份", "營運成本", "建立人", "建立時間", "操作"]}
            columns={[
              (item) => moment(item.month).format("yyyy-MM"),
              (item) => thousandsSeparator(item.cost),
              (item) => item.createTo.fullName,
              (item) => moment(item.createdAt).format("yyyy-MM-DD"),
              (item) => (
                <Button
                  color="primary"
                  variant="contained"
                  onPress={(e) => {
                    e.stopPropagation();
                    Alert.alert("通知", "確定要刪除？", [
                      {
                        text: "確定",
                        onPress: () => {
                          deleteAccountManagement({
                            variables: { id: item.id },
                          });
                        },
                        type: "ok",
                      },
                      {
                        text: "取消",
                        type: "cancel",
                      },
                    ]);
                  }}
                >
                  刪除
                </Button>
              ),
            ]}
            onPress={(item) => {
              changeData("open", true);
              changeData("id", Number(item.id), true);
            }}
          />
        </CardContent>
        <CardActions>
          <Grid container justifyContent="center">
            <Pagination
              count={
                data.accountManagements.pageCount
                  ? data.accountManagements.pageCount
                  : 1
              }
              size="large"
              color="primary"
              page={newData.page}
              onChange={(e, n) => {
                changeData("page", n, true);
              }}
            />
          </Grid>
        </CardActions>
      </Card>
    );
  }
}

function OperatingCostDialog({ id, open, onClose = () => {} }) {
  const Alert = useAlert();
  const [newData, setNewData] = useState({
    month: new Date(),
    cost: "",
    file: null,
    createTo: "",
  });

  function changeDataListener(key, v) {}
  function changeData(key, v, rerender = false) {
    if (rerender) setNewData((prev) => ({ ...prev, [key]: v }));
    else setNewData((prev) => Object.assign(prev, { [key]: v }));
    if (typeof changeDataListener === "function") changeDataListener(key, v);
  }

  const [getAccountManagement, { loading: accountManagementLoading }] =
    useLazyQuery(GET_ACCOUNTMANAGEMENT, {
      fetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
      onCompleted({ accountManagement }) {
        if (accountManagement) {
          const { __typename, ...file } = accountManagement.file;
          changeData("month", accountManagement.month);
          changeData("cost", accountManagement.cost);
          changeData("file", file);
          changeData("createTo", accountManagement.createTo.fullName, true);
        }
      },
      onError(error) {
        return Alert.notify(`${error}`);
      },
    });

  const [saveAccountManagement, { loading: saveAccountManagementLoading }] =
    useMutation(SAVE_ACCOUNTMANAGEMENT, {
      onCompleted({ saveAccountManagement }) {
        if (saveAccountManagement) {
          Alert.alert("", "儲存成功", [
            {
              text: "確定",
              onPress: () => {
                onClose("refetch");
              },
              type: "ok",
            },
          ]);
        } else {
          Alert.alert("", "儲存失敗，請再次嘗試。", [
            { text: "確定", type: "ok" },
          ]);
        }
      },
      onError(error) {
        Alert.notice(`${error.message.replace("GraphQL error: ", "")}`);
      },
    });

  const [uploadFile, { loading: uploadFileLoading }] = useMutation(
    UPLOAD_FILE,
    {
      onCompleted({ uploadFile }) {
        delete uploadFile.__typename;
        newData.file = uploadFile;
        changeData("file", newData.file, true);
      },
    }
  );

  function save() {
    const accountManagementInput = {
      createToUserId: Number(localStorage.getItem("@fullhouseUserId")),
      month: newData.month,
      cost: Number(newData.cost),
      file: newData.file,
    };
    if (id) {
      accountManagementInput.id = id;
    }
    saveAccountManagement({ variables: { accountManagementInput } });
  }

  useEffect(() => {
    if (id) {
      getAccountManagement({ variables: { id } });
    }
  }, [id]);
  return (
    <Dialog onClose={onClose} open={open} fullWidth maxWidth={"sm"}>
      <LoadingModal
        loading={
          saveAccountManagementLoading ||
          uploadFileLoading ||
          accountManagementLoading
        }
      />
      <DialogTitle>{`${id ? "編輯" : "新增"}營運成本`}</DialogTitle>
      <Divider />
      <DialogContent dividers>
        <Grid container spacing={1} direction="column">
          <Grid item>
            <Typography color="primary">營運月份</Typography>
            <DatePicker
              views={["year", "month"]}
              autoOk={true}
              value={newData.dateMonth}
              minDate={new Date()}
              format="yyyy年MM月"
              onChange={(v) => {
                changeData("month", v, true);
              }}
              fullWidth
            />
          </Grid>
          <Grid item>
            <Typography color="primary"> 營運成本</Typography>
            <NumberInput
              value={newData.cost}
              onChange={(v) => {
                changeData("cost", v, true);
              }}
              fullWidth
              thousandSeparator
            />
          </Grid>
          <Grid item>
            <Typography color="primary">上傳檔案</Typography>
            <Grid container spacing={1}>
              {newData.file?.filename ? (
                <ButtonBase
                  onClick={() =>
                    window.open(
                      `${newData.file.location}`,
                      "_blank",
                      "noopener"
                    )
                  }
                >
                  <Typography>{newData.file.filename}</Typography>
                </ButtonBase>
              ) : null}
              <IconButton component="label" for="certificateUpload">
                <AttachFileIcon />
              </IconButton>
              <input
                type="file"
                id="certificateUpload"
                style={{ display: "none" }}
                onChange={(event) => {
                  const {
                    target: { validity, files },
                  } = event;

                  if (validity.valid) {
                    for (let file of files) {
                      uploadFile({ variables: { file } });
                    }
                  }
                }}
              />
            </Grid>
          </Grid>
          <Grid item>
            <Typography color="primary"> 營運店家</Typography>
            <Typography>
              {localStorage.getItem("@fullhouseUserBranch")}
            </Typography>
          </Grid>
          <Grid item>
            <Typography color="primary"> 建立人</Typography>
            <Typography>
              {id
                ? newData.createTo
                : localStorage.getItem("@fullhouseUserFullName")}
            </Typography>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button color="primary" variant="contained" onPress={save}>
          送出
        </Button>
      </DialogActions>
    </Dialog>
  );
}
